/**
 * PlayerPropHitRateCard
 * Line-aware settled hit rate for a player's prop, per direction, in two columns:
 * the global model sample and the user's own tracked bets. Headline = how often the
 * actual value cleared the target line; the naive pick-hit rate is shown as a subnote.
 */

import type {
  ActionNetworkRead,
  LineAwareRate,
  PlayerPropHitRateCardData,
} from '@parlay-picker/shared/chatBettingCards';
import { INDEPENDENT_MODEL_LABEL } from '@parlay-picker/shared/chatBettingCards';
import { getLeagueLabel, isLeagueKey } from '@parlay-picker/shared/league';
import { Target } from 'lucide-react';
import type { ReactElement } from 'react';

const NO_DATA = '—';
const LOW_SAMPLE = 10;

/** Headline is the clears rate; falls back to the naive rate when no line was resolved. */
function headlinePct(la: LineAwareRate | null): string {
  const r = la?.clears ?? la?.naive ?? null;
  if (!r || r.total === 0 || r.hitRate == null) return NO_DATA;
  return `${(r.hitRate * 100).toFixed(0)}%`;
}

/** Sample count + (naive rate when clears is shown, or a "pick-hit" tag when it isn't). */
function subNote(la: LineAwareRate | null): string {
  if (!la) return '';
  const r = la.clears ?? la.naive;
  if (r.total === 0) return '';
  const bits = [`${r.hits}/${r.total}`];
  if (la.clears && la.naive.hitRate != null)
    bits.push(`naive ${(la.naive.hitRate * 100).toFixed(0)}%`);
  else if (!la.clears) bits.push('pick-hit');
  return bits.join(' · ');
}

function personalIsLow(la: LineAwareRate | null): boolean {
  const r = la?.clears ?? la?.naive ?? null;
  return r != null && r.total > 0 && r.total < LOW_SAMPLE;
}

/**
 * Independent second model's read for one direction. Generic label only (upstream source is
 * never shown in-app). This is a WEAK, secondary signal (validated near-noise): shown as a
 * differing second opinion in muted text, NOT an alarm/veto over our own model score.
 */
function IndependentModelLine({
  an,
  ourLine,
}: {
  an: ActionNetworkRead | undefined;
  ourLine: number | null;
}): ReactElement | null {
  if (!an) return null;
  if (an.coverage !== 'covered') {
    return (
      <div className="text-[9px] text-theme-text-tertiary">
        {INDEPENDENT_MODEL_LABEL}: not covered
      </div>
    );
  }
  const proj = an.projection != null ? `proj ${an.projection.toFixed(1)}` : null;
  const showAnLine = an.anLine != null && ourLine != null && Math.abs(an.anLine - ourLine) > 1e-9;
  const bits = [proj, an.grade].filter(Boolean).join(' · ');
  return (
    <div className="text-[9px] text-theme-text-tertiary">
      {INDEPENDENT_MODEL_LABEL}: {bits || 'no read'}
      {showAnLine ? ` at ${an.anLine}` : ''}
      {an.conflict ? ' · differs from your lean (weak signal)' : ''}
    </div>
  );
}

export function PlayerPropHitRateCard({ data }: { data: PlayerPropHitRateCardData }): ReactElement {
  const { playerName, propType, league, sportsbook, directions } = data;
  const anyStale = directions.some((d) => d.lineIsStale || d.lineSource === 'pick');
  const anyLow = directions.some((d) => personalIsLow(d.personal));

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Target className="w-4 h-4 text-theme-primary" />
        <span className="text-sm font-semibold text-theme-text">
          {playerName} {propType}
        </span>
        <span className="text-[10px] text-theme-text-tertiary ml-auto">
          {sportsbook} · {isLeagueKey(league) ? getLeagueLabel(league) : league}
        </span>
      </div>

      {/* Column header */}
      <div className="grid grid-cols-3 gap-1 text-[9px] text-theme-text-tertiary">
        <span>Clears line</span>
        <span className="text-center">Model</span>
        <span className="text-center">You</span>
      </div>

      {/* One block per direction: the 3-col rate row + the independent-model line. */}
      <div className="space-y-2">
        {directions.map((row) => (
          <div key={row.direction} className="space-y-0.5">
            <div className="grid grid-cols-3 gap-1 items-start text-xs">
              <span className="text-theme-text capitalize">
                {row.direction}
                {row.line != null && <span className="text-theme-text-tertiary"> {row.line}</span>}
              </span>
              <span className="text-center tabular-nums">
                <span className="text-theme-text font-medium">{headlinePct(row.global)}</span>
                {subNote(row.global) && (
                  <span className="block text-theme-text-tertiary text-[9px]">
                    {subNote(row.global)}
                  </span>
                )}
              </span>
              <span className="text-center tabular-nums">
                <span className="text-theme-text font-medium">{headlinePct(row.personal)}</span>
                {subNote(row.personal) && (
                  <span className="block text-theme-text-tertiary text-[9px]">
                    {subNote(row.personal)}
                    {personalIsLow(row.personal) ? '*' : ''}
                  </span>
                )}
              </span>
            </div>
            <IndependentModelLine an={row.actionNetwork} ourLine={row.line} />
          </div>
        ))}
      </div>

      <div className="text-[10px] text-theme-text-tertiary">
        Clears = how often the actual value beat the line; naive = the pick hit its own line.{' '}
        {NO_DATA} means no settled history.
        {anyStale ? ' Line from a recent pick, not the live book.' : ''}
        {anyLow ? ' * small sample.' : ''}
      </div>
    </div>
  );
}
